import { useState, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@features/auth/hooks/useAdminAuth';
import { useUI } from '@shared/hooks/useUI';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from '@shared/components/ui/Modal';
import { Button } from '@shared/components/ui/Button';

/**
 * Admin Sidebar Component
 * Pixel-perfect recreation of Angular sidebar.component.html
 * Source: /FrontEnd/src/app/common/sidebar/sidebar.component.html
 */
export const AdminSidebar = () => {
    const location = useLocation();
    const { logout } = useAdminAuth();
    const { sidebarOpen, setSidebarOpen } = useUI();
    const [openSection, setOpenSection] = useState<string | null>(null);
    const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

    // Dynamic Route Sync: Auto-expand section based on current path
    useEffect(() => {
        const path = location.pathname;
        if (path.startsWith('/dashboard/brands')) setOpenSection('brands');
        else if (path.startsWith('/dashboard/category')) setOpenSection('category');
        else if (path.startsWith('/dashboard/sub-category')) setOpenSection('sub-category');
        else if (path.startsWith('/dashboard/products')) setOpenSection('products');
        else if (path.startsWith('/dashboard/sales')) setOpenSection('sales');
        else if (path.startsWith('/dashboard/carriers')) setOpenSection('carriers');
        else if (path.startsWith('/dashboard/bespoke-services')) setOpenSection('bespoke');
        else if (path.startsWith('/dashboard/sliders')) setOpenSection('sliders');
        else {
            // Close sections for top-level links or unknown paths
            setOpenSection(null);
        }
    }, [location.pathname]);

    const toggleSection = (section: string) => {
        setOpenSection(openSection === section ? null : section);
    };

    const isSectionOpen = (section: string) => openSection === section;

    const handleLogout = () => {
        setIsLogoutDialogOpen(true);
    };

    const isActive = (path: string, exact = false) => {
        if (exact) {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    const handleLinkClick = useCallback(() => {
        if (window.innerWidth < 1024) {
            setSidebarOpen(false);
        }
    }, [setSidebarOpen]);

    return (
        <div
            className={`sidebar-area bg-white dark:bg-[#0c1427] fixed overflow-hidden z-[100] top-0 h-screen rounded-r-md border-r border-gray-100 dark:border-[#172036] shadow-md ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}
        >
            <div className="logo bg-white dark:bg-[#0c1427] border-b border-gray-100 dark:border-[#172036] px-[25px] pt-[19px] pb-[15px] absolute z-[2] right-0 top-0 left-0">
                <Link to="/dashboard/home" className="transition-none relative flex items-center" onClick={handleLinkClick}>
                    <img src="/images/logo-icon.png" alt="BoostingYourLifestyle Logo" className="w-10 h-10 object-contain" />
                    <span className={`font-bold text-black dark:text-white relative ltr:ml-[8px] rtl:mr-[8px] top-px text-lg tracking-wider ${!sidebarOpen && 'hidden'}`}>
                        Lifestyle
                    </span>
                </Link>

                {/* Close Button for Mobile */}
                {sidebarOpen && (
                    <button
                        type="button"
                        className="lg:hidden absolute right-4 top-5 text-gray-500 hover:text-primary-500 transition-all"
                        onClick={() => setSidebarOpen(false)}
                    >
                        <i className="ri-close-line text-2xl"></i>
                    </button>
                )}
            </div>

            <div className="pt-[89px] px-[25px] pb-[20px] h-full overflow-y-auto sidebar-scrollbar">
                <div className="accordion">
                    <span className="block relative font-medium uppercase text-gray-400 mb-[10px] text-xs">
                        Main
                    </span>

                    {/* Dashboard Link */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <Link
                            to="/dashboard/home"
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/home', true) ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-dashboard-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Dashboard</span>
                        </Link>
                    </div>

                    {/* Home Link */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <Link
                            to="/"
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/', true) ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-home-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Home</span>
                        </Link>
                    </div>

                    {/* Orders Link */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <Link
                            to="/dashboard/orders"
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/orders') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-shopping-cart-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Orders</span>
                        </Link>
                    </div>

                    <span className="block relative font-medium uppercase text-gray-400 mb-[10px] text-xs mt-[22px]">
                        Catalog
                    </span>

                    {/* Brands Dropdown */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <button
                            onClick={() => toggleSection('brands')}
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] text-left ${isSectionOpen('brands') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-price-tag-3-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Brands</span>
                            <i className={`ri-arrow-down-s-line arrow transition-all absolute right-[10px] text-[18px] ${isSectionOpen('brands') ? 'rotate-180' : ''} ${!sidebarOpen && 'hidden'}`}></i>
                        </button>
                        <ul className={`accordion-content overflow-hidden transition-all ${isSectionOpen('brands') ? 'max-h-[500px]' : 'max-h-0'} ${!sidebarOpen && 'hidden'}`}>
                            <li>
                                <Link to="/dashboard/brands/add" className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/brands/add') ? 'text-red-600' : ''}`}>
                                    Add Brand
                                </Link>
                            </li>
                            <li>
                                <Link to="/dashboard/brands/list" className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/brands/list') ? 'text-red-600' : ''}`}>
                                    Brand List
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Category Dropdown */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <button
                            onClick={() => toggleSection('category')}
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] text-left ${isSectionOpen('category') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-layout-grid-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Category</span>
                            <i className={`ri-arrow-down-s-line arrow transition-all absolute right-[10px] text-[18px] ${isSectionOpen('category') ? 'rotate-180' : ''} ${!sidebarOpen && 'hidden'}`}></i>
                        </button>
                        <ul className={`accordion-content overflow-hidden transition-all ${isSectionOpen('category') ? 'max-h-[500px]' : 'max-h-0'} ${!sidebarOpen && 'hidden'}`}>
                            <li>
                                <Link to="/dashboard/category/add" className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/category/add') ? 'text-red-600' : ''}`}>
                                    Add Category
                                </Link>
                            </li>
                            <li>
                                <Link to="/dashboard/category/list" className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/category/list') ? 'text-red-600' : ''}`}>
                                    Category List
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Sub-Category Dropdown */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <button
                            onClick={() => toggleSection('sub-category')}
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] text-left ${isSectionOpen('sub-category') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-git-repository-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Sub-Category</span>
                            <i className={`ri-arrow-down-s-line arrow transition-all absolute right-[10px] text-[18px] ${isSectionOpen('sub-category') ? 'rotate-180' : ''} ${!sidebarOpen && 'hidden'}`}></i>
                        </button>
                        <ul className={`accordion-content overflow-hidden transition-all ${isSectionOpen('sub-category') ? 'max-h-[500px]' : 'max-h-0'} ${!sidebarOpen && 'hidden'}`}>
                            <li>
                                <Link to="/dashboard/sub-category/add" className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/sub-category/add') ? 'text-red-600' : ''}`}>
                                    Add Sub-Category
                                </Link>
                            </li>
                            <li>
                                <Link to="/dashboard/sub-category/list" className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/sub-category/list') ? 'text-red-600' : ''}`}>
                                    Sub-Category List
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Products Dropdown */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <button
                            onClick={() => toggleSection('products')}
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] text-left ${isSectionOpen('products') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-archive-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Products</span>
                            <i className={`ri-arrow-down-s-line arrow transition-all absolute right-[10px] text-[18px] ${isSectionOpen('products') ? 'rotate-180' : ''} ${!sidebarOpen && 'hidden'}`}></i>
                        </button>
                        <ul className={`accordion-content overflow-hidden transition-all ${isSectionOpen('products') ? 'max-h-[500px]' : 'max-h-0'} ${!sidebarOpen && 'hidden'}`}>
                            <li>
                                <Link to="/dashboard/products/add" className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/products/add') ? 'text-red-600' : ''}`}>
                                    Add Product
                                </Link>
                            </li>
                            <li>
                                <Link to="/dashboard/products/list" className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/products/list') ? 'text-red-600' : ''}`}>
                                    Product List
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Inventory Link */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <Link
                            to="/dashboard/inventory-management"
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/inventory-management') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-stack-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Inventory</span>
                        </Link>
                    </div>

                    <span className="block relative font-medium uppercase text-gray-400 mb-[10px] text-xs mt-[22px]">
                        Marketing & Logistics
                    </span>

                    {/* Sales Dropdown */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <button
                            onClick={() => toggleSection('sales')}
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] text-left ${isSectionOpen('sales') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-percent-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Sales</span>
                            <i className={`ri-arrow-down-s-line arrow transition-all absolute right-[10px] text-[18px] ${isSectionOpen('sales') ? 'rotate-180' : ''} ${!sidebarOpen && 'hidden'}`}></i>
                        </button>
                        <ul className={`accordion-content overflow-hidden transition-all ${isSectionOpen('sales') ? 'max-h-[500px]' : 'max-h-0'} ${!sidebarOpen && 'hidden'}`}>
                            <li>
                                <Link to="/dashboard/sales/add" onClick={handleLinkClick} className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/sales/add') ? 'text-red-600' : ''}`}>
                                    Add Sale
                                </Link>
                            </li>
                            <li>
                                <Link to="/dashboard/sales/list" onClick={handleLinkClick} className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/sales/list') ? 'text-red-600' : ''}`}>
                                    Sale List
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Carriers Dropdown */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <button
                            onClick={() => toggleSection('carriers')}
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] text-left ${isSectionOpen('carriers') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-truck-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Carriers</span>
                            <i className={`ri-arrow-down-s-line arrow transition-all absolute right-[10px] text-[18px] ${isSectionOpen('carriers') ? 'rotate-180' : ''} ${!sidebarOpen && 'hidden'}`}></i>
                        </button>
                        <ul className={`accordion-content overflow-hidden transition-all ${isSectionOpen('carriers') ? 'max-h-[500px]' : 'max-h-0'} ${!sidebarOpen && 'hidden'}`}>
                            <li>
                                <Link to="/dashboard/carriers/add" onClick={handleLinkClick} className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/carriers/add') ? 'text-red-600' : ''}`}>
                                    Add Carrier
                                </Link>
                            </li>
                            <li>
                                <Link to="/dashboard/carriers/list" onClick={handleLinkClick} className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/carriers/list') ? 'text-red-600' : ''}`}>
                                    Carrier List
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <span className="block relative font-medium uppercase text-gray-400 mb-[10px] text-xs mt-[22px]">
                        Content
                    </span>

                    {/* Bespoke Services Dropdown */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <button
                            onClick={() => toggleSection('bespoke')}
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] text-left ${isSectionOpen('bespoke') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-magic-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Bespoke Services</span>
                            <i className={`ri-arrow-down-s-line arrow transition-all absolute right-[10px] text-[18px] ${isSectionOpen('bespoke') ? 'rotate-180' : ''} ${!sidebarOpen && 'hidden'}`}></i>
                        </button>
                        <ul className={`accordion-content overflow-hidden transition-all ${isSectionOpen('bespoke') ? 'max-h-[500px]' : 'max-h-0'} ${!sidebarOpen && 'hidden'}`}>
                            <li>
                                <Link to="/dashboard/bespoke-services/add" onClick={handleLinkClick} className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/bespoke-services/add') ? 'text-red-600' : ''}`}>
                                    Add Service
                                </Link>
                            </li>
                            <li>
                                <Link to="/dashboard/bespoke-services/list" onClick={handleLinkClick} className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/bespoke-services/list') ? 'text-red-600' : ''}`}>
                                    Services List
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Sliders Dropdown */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <button
                            onClick={() => toggleSection('sliders')}
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] text-left ${isSectionOpen('sliders') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-slideshow-3-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Sliders</span>
                            <i className={`ri-arrow-down-s-line arrow transition-all absolute right-[10px] text-[18px] ${isSectionOpen('sliders') ? 'rotate-180' : ''} ${!sidebarOpen && 'hidden'}`}></i>
                        </button>
                        <ul className={`accordion-content overflow-hidden transition-all ${isSectionOpen('sliders') ? 'max-h-[500px]' : 'max-h-0'} ${!sidebarOpen && 'hidden'}`}>
                            <li>
                                <Link to="/dashboard/sliders/add" className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/sliders/add') ? 'text-red-600' : ''}`}>
                                    Add Slider
                                </Link>
                            </li>
                            <li>
                                <Link to="/dashboard/sliders/list" className={`flex items-center py-[8px] pl-[43px] rounded-md transition-all hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/sliders/list') ? 'text-red-600' : ''}`}>
                                    Slider List
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* About Us Link */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <Link
                            to="/dashboard/about-us"
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/about-us') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-information-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>About Us</span>
                        </Link>
                    </div>

                    <span className="block relative font-medium uppercase text-gray-400 mb-[10px] text-xs mt-[22px]">
                        Settings
                    </span>

                    {/* Email Settings Link */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <Link
                            to="/dashboard/settings/email"
                            onClick={handleLinkClick}
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/settings/email') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-mail-settings-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Email Settings</span>
                        </Link>
                    </div>

                    {/* Tax Settings Link */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <Link
                            to="/dashboard/settings/tax"
                            onClick={handleLinkClick}
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/settings/tax') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-percent-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Tax</span>
                        </Link>
                    </div>

                    {/* Shipping Settings Link */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <Link
                            to="/dashboard/settings/shipping"
                            onClick={handleLinkClick}
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/settings/shipping') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-truck-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>Shipping</span>
                        </Link>
                    </div>



                    <span className="block relative font-medium uppercase text-gray-400 mb-[10px] text-xs mt-[22px]">
                        Account
                    </span>

                    {/* Profile Link */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <Link
                            to="/dashboard/profile"
                            className={`accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] ${isActive('/dashboard/profile') ? 'bg-gray-50 dark:bg-[#15203c]' : ''}`}
                        >
                            <i className="ri-user-line transition-all text-gray-500 dark:text-gray-400 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none ${!sidebarOpen && 'hidden'}`}>My Profile</span>
                        </Link>
                    </div>

                    {/* Sign Out Link */}
                    <div className="accordion-item rounded-md text-black dark:text-white mb-[5px] whitespace-nowrap">
                        <button
                            onClick={handleLogout}
                            className="accordion-button flex items-center transition-all py-[9px] px-[14px] rounded-md font-medium w-full relative hover:bg-gray-50 dark:hover:bg-[#15203c] text-left cursor-pointer"
                        >
                            <i className="ri-logout-box-r-line transition-all text-red-500 mr-[10px] text-[20px] leading-none relative -top-px"></i>
                            <span className={`title leading-none text-red-600 ${!sidebarOpen && 'hidden'}`}>Logout</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Logout Confirmation Dialog */}
            <Dialog open={isLogoutDialogOpen} onOpenChange={setIsLogoutDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Sign Out</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to log out of the admin panel? You will need to sign in again to access the dashboard.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setIsLogoutDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            className="bg-red-600 hover:bg-red-700 text-white"
                            onClick={() => {
                                setIsLogoutDialogOpen(false);
                                logout();
                            }}
                        >
                            Log Out
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
